/**
 * Reading what a media file is from its own bytes, without decoding it and without loading the audio engine:
 * is it audio at all, does a video have a sound track, and what sample rate does an M4A/MP4 hold. Everything
 * reads from the start of the file (and, for MP4, the `moov` box wherever it sits) with Blob.slice, so a 2 GB
 * recording costs a few kilobytes of reading. Pure JavaScript, so it also runs in Node (and on a server).
 */
import { sniffSampleRate } from "./sniffRate";

const ascii = (b: Uint8Array, o: number, n: number) => String.fromCharCode(...Array.from(b.subarray(o, o + n)));
const u32 = (b: Uint8Array, o: number) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
const u16 = (b: Uint8Array, o: number) => (b[o] << 8) | b[o + 1];

async function readBytes(blob: Blob, start: number, length: number): Promise<Uint8Array> {
  return new Uint8Array(await blob.slice(start, Math.min(blob.size, start + length)).arrayBuffer());
}

/**
 * What kind of audio container the first bytes look like, or null when they look like nothing audio-related
 * (a text file renamed .mp3, an image, an empty file). A file the browser cannot play but that has a real audio
 * signature (WMA, AMR, AC3 ...) may still be convertible by ffmpeg, so only null means "not audio".
 */
export function sniffAudioKind(head: Uint8Array): string | null {
  const tag = (o: number, s: string) => head.length >= o + s.length && ascii(head, o, s.length) === s;
  if (tag(0, "RIFF") && tag(8, "WAVE")) return "wav";
  if (tag(0, "fLaC")) return "flac";
  if (tag(0, "OggS")) return "ogg";
  if (tag(4, "ftyp")) return "mp4";
  if (tag(0, "FORM") && (tag(8, "AIFF") || tag(8, "AIFC"))) return "aiff";
  if (tag(0, "#!AMR")) return "amr";
  if (tag(0, "caff")) return "caf";
  if (tag(0, "wvpk")) return "wavpack";
  if (tag(0, "MAC ")) return "ape";
  if (tag(0, "FRM8") || tag(0, "DSD ")) return "dsd";
  if (head.length >= 4 && head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) return "matroska";
  if (head.length >= 16 && head[0] === 0x30 && head[1] === 0x26 && head[2] === 0xb2 && head[3] === 0x75) return "asf"; // WMA / WMV
  if (head.length >= 2 && head[0] === 0x0b && head[1] === 0x77) return "ac3";
  let o = 0;
  if (tag(0, "ID3") && head.length > 10) o = 10 + (((head[6] & 0x7f) << 21) | ((head[7] & 0x7f) << 14) | ((head[8] & 0x7f) << 7) | (head[9] & 0x7f));
  // MPEG audio frame (MP3, MP2) or ADTS AAC: a sync word followed by a plausible header, within the first few KB.
  for (let k = o; k < Math.min(head.length - 4, o + 8192); k++) {
    if (head[k] !== 0xff || (head[k + 1] & 0xe0) !== 0xe0) continue;
    const ver = (head[k + 1] >> 3) & 3, layer = (head[k + 1] >> 1) & 3, idx = (head[k + 2] >> 2) & 3, br = head[k + 2] >> 4;
    if ((head[k + 1] & 0xf6) === 0xf0 && idx < 13) return "aac"; // ADTS
    if (ver !== 1 && layer !== 0 && idx !== 3 && br !== 15 && br !== 0) return "mp3";
  }
  if (o > 10) return "mp3"; // an ID3 tag with nothing readable after it in the head: still an MP3-family file
  return null;
}

/** Reads the head of a file and says what audio container it is, or null (see sniffAudioKind). */
export async function sniffFileKind(file: Blob): Promise<string | null> {
  return sniffAudioKind(await readBytes(file, 0, 16384));
}

// ---------- MP4 / MOV / M4A ----------

interface Box { type: string; start: number; payload: number; end: number }

function boxesIn(buf: Uint8Array, start: number, end: number): Box[] {
  const out: Box[] = [];
  let o = start;
  while (o + 8 <= end) {
    let size = u32(buf, o);
    const type = ascii(buf, o + 4, 4);
    let header = 8;
    if (size === 1 && o + 16 <= end) { size = u32(buf, o + 8) * 2 ** 32 + u32(buf, o + 12); header = 16; }
    else if (size === 0) size = end - o;
    if (size < header || o + size > end) break;
    out.push({ type, start: o, payload: o + header, end: o + size });
    o += size;
  }
  return out;
}

export interface Mp4Track { kind: string; /** Sample rate of an audio track, from its sample entry. */ sampleRate: number | null }

/** Tracks listed in a `moov` box held in memory: "soun" (sound), "vide" (picture), "text", ... */
export function parseMoovTracks(moov: Uint8Array): Mp4Track[] {
  const tracks: Mp4Track[] = [];
  const top = boxesIn(moov, 0, moov.length).find((b) => b.type === "moov");
  if (!top) return tracks;
  for (const trak of boxesIn(moov, top.payload, top.end).filter((b) => b.type === "trak")) {
    const mdia = boxesIn(moov, trak.payload, trak.end).find((b) => b.type === "mdia");
    if (!mdia) continue;
    const parts = boxesIn(moov, mdia.payload, mdia.end);
    const hdlr = parts.find((b) => b.type === "hdlr");
    if (!hdlr) continue;
    const kind = ascii(moov, hdlr.payload + 8, 4);
    let sampleRate: number | null = null;
    if (kind === "soun") {
      const minf = parts.find((b) => b.type === "minf");
      const stbl = minf && boxesIn(moov, minf.payload, minf.end).find((b) => b.type === "stbl");
      const stsd = stbl && boxesIn(moov, stbl.payload, stbl.end).find((b) => b.type === "stsd");
      if (stsd) {
        // stsd: version+flags (4), entry count (4), then the sample entries.
        const entry = boxesIn(moov, stsd.payload + 8, stsd.end)[0];
        if (entry && entry.start + 36 <= entry.end) {
          // AudioSampleEntry: header (8) + reserved/data-reference (8) + version, revision, vendor (8) + channels (2) + sample size (2) + 4 + rate 16.16
          const rate = u16(moov, entry.start + 32);
          sampleRate = rate || null;
        }
      }
    }
    tracks.push({ kind, sampleRate });
  }
  return tracks;
}

/** Finds and reads the `moov` box of an MP4-family file by walking the top-level boxes (skipping the big `mdat`). Null when there is none. */
export async function readMoov(file: Blob, maxBytes = 64 * 1024 * 1024): Promise<Uint8Array | null> {
  let o = 0;
  for (let guard = 0; guard < 200 && o + 8 <= file.size; guard++) {
    const h = await readBytes(file, o, 16);
    if (h.length < 8) return null;
    let size = u32(h, 0);
    const type = ascii(h, 4, 4);
    if (size === 1 && h.length >= 16) size = u32(h, 8) * 2 ** 32 + u32(h, 12);
    else if (size === 0) size = file.size - o;
    if (size < 8) return null;
    if (type === "moov") return size <= maxBytes ? readBytes(file, o, size) : null;
    o += size;
  }
  return null;
}

/** Tracks of an MP4/MOV/M4A file, or null when it is not one (or the header is unreadable). */
export async function mp4Tracks(file: Blob): Promise<Mp4Track[] | null> {
  const head = await readBytes(file, 0, 12);
  if (ascii(head, 4, 4) !== "ftyp") return null;
  const moov = await readMoov(file);
  return moov ? parseMoovTracks(moov) : null;
}

// ---------- WebM / MKV ----------

/** WebM/MKV: the track list sits in the first kilobytes; codec ids start with "A_" for sound and "V_" for pictures. */
export function matroskaHasAudio(head: Uint8Array): boolean | null {
  const text = Array.from(head, (c) => String.fromCharCode(c)).join("");
  if (/A_(OPUS|VORBIS|AAC|MPEG|FLAC|PCM|AC3|EAC3|DTS|TRUEHD|MS)/.test(text)) return true;
  if (/V_(VP8|VP9|AV1|MPEG|MS|UNCOMPRESSED|THEORA|REAL)/.test(text)) return false;
  return null;
}

/**
 * Does this video have a sound track? true / false when the container says so (MP4, MOV, M4V, WebM, MKV),
 * null when the format is not one this can read (the caller then finds out by running ffmpeg).
 */
export async function videoHasAudio(file: Blob): Promise<boolean | null> {
  try {
    const head = await readBytes(file, 0, 262144);
    if (ascii(head, 4, 4) === "ftyp") {
      const tracks = await mp4Tracks(file);
      return tracks ? tracks.some((t) => t.kind === "soun") : null;
    }
    if (head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) return matroskaHasAudio(head);
    return null;
  } catch { return null; }
}

/**
 * The file's own sample rate, read from its header (WAV, FLAC, OGG Vorbis, MP3, and M4A/MP4 via the sample
 * entry), or null for formats that state none (Opus). The browser decodes to the device rate (often 48 kHz), so
 * the rate has to come from the file itself, then be passed to the decoder so nothing is resampled on the way in.
 */
export async function fileSampleRate(file: Blob): Promise<number | null> {
  try {
    const head = await readBytes(file, 0, 65536);
    if (ascii(head, 4, 4) === "ftyp") {
      const tracks = await mp4Tracks(file);
      return tracks?.find((t) => t.kind === "soun")?.sampleRate ?? null;
    }
    return sniffSampleRate(head);
  } catch { return null; }
}
